import React, { useEffect, useState } from "react";
import { Input, Select, Space, Switch, Typography } from "antd";
import { mainTypesPrestation } from "../../../../utils/typesPrestation";
import { CONFIG_LABELS } from "../institutionMerge.constants";

const { Text } = Typography;

const OID = /^[a-f0-9]{24}$/i;

/**
 * Human rendering of a configuration value. Same rules as MergeSummary — copied on purpose,
 * the steps stay independent of each other.
 */
export const show = (v, names = {}) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (Array.isArray(v))
    return v.length ? v.map(id => names[id] || String(id)).join(", ") : "—";
  if (typeof v === "string" && OID.test(v) && names[v]) return names[v];
  if (typeof v === "object" && "cloturePrestation" in v)
    return `clôture prestation : ${v.cloturePrestation ? "oui" : "non"}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/** Identity of a value for radios and comparisons: order-insensitive for id arrays, null for "nothing". */
export const valueKey = v =>
  JSON.stringify(
    v === undefined ? null : Array.isArray(v) ? v.map(String).sort() : v,
  );

/**
 * Field descriptors, in display order. `preview.config.meta` when the server sends it;
 * older previews only get labels (radios among existing values, no custom control).
 */
export const fieldMetas = cfg => {
  const fields = cfg.fields || {};
  const skip = new Set(["name", "billings"]);
  if (Array.isArray(cfg.meta) && cfg.meta.length)
    return cfg.meta
      .filter(m => m && !skip.has(m.field) && fields[m.field])
      .map(m => ({
        ...m,
        label: m.label || CONFIG_LABELS[m.field] || m.field,
      }));
  return Object.keys(fields)
    .filter(f => !skip.has(f))
    .map(field => ({
      field,
      label: CONFIG_LABELS[field] || field,
      kind: null,
    }));
};

/** id → label: the server's refNames, completed with the names of every selectable option. */
export const nameMap = cfg => {
  const names = { ...(cfg.refNames || {}) };
  for (const list of Object.values(cfg.options || {}))
    for (const o of list || [])
      if (o && o._id && !names[o._id]) names[o._id] = o.name;
  return names;
};

/** Distinct before-values of one field, first-seen order, with the institutions holding each. */
export const distinctValues = (values = []) => {
  const out = [];
  for (const v of values) {
    const k = valueKey(v.value);
    const hit = out.find(d => d.key === k);
    if (hit) hit.holders.push(v.sourceName);
    else out.push({ key: k, value: v.value, holders: [v.sourceName] });
  }
  return out;
};

const refList = (meta, options) =>
  options[meta.ref || (meta.kind === "refs" ? "Tag" : "")] || [];

/** Can the operator enter a value that no institution has today? */
export const canCustom = (meta, options = {}, distinct = []) => {
  switch (meta.kind) {
    case "text":
    case "number":
    case "image":
      return true;
    case "enum":
      return meta.field === "mainType" || (meta.options || []).length > 0;
    case "ref":
    case "refs":
      return refList(meta, options).length > 0;
    case "boolean":
    case "notification":
      // both states already listed as radios → nothing else to offer
      return distinct.length < 2;
    default:
      return false;
  }
};

/** For a boolean field « Autre… » can only mean the state nobody has today. */
export const flippedValue = (meta, distinct) => {
  const cur = distinct[0] ? distinct[0].value : undefined;
  if (meta.kind === "boolean") return cur !== true;
  return { cloturePrestation: !(cur && cur.cloturePrestation !== false) };
};

const enumOptions = (meta, before) => {
  const list =
    meta.field === "mainType"
      ? mainTypesPrestation.map(o => ({ ...o }))
      : (meta.options || []).map(o => ({ ...o }));
  // a legacy value still in the database must stay selectable (and readable)
  const known = new Set(list.map(o => o.value));
  for (const v of before)
    if (typeof v === "string" && v && !known.has(v)) {
      known.add(v);
      list.push({ value: v, label: v });
    }
  const flat = [];
  const groups = new Map();
  for (const o of list) {
    const opt = { value: o.value, label: o.label || o.value };
    if (!o.group) flat.push(opt);
    else {
      if (!groups.has(o.group)) groups.set(o.group, []);
      groups.get(o.group).push(opt);
    }
  }
  return [
    ...flat,
    ...[...groups].map(([label, options]) => ({ label, options })),
  ];
};

const refOptions = (list, withInstitution) =>
  list.map(o => ({
    value: String(o._id),
    search: `${o.name || ""} ${o.institutionName || ""}`.toLowerCase(),
    label:
      withInstitution && o.institutionName ? (
        <span>
          {o.name} <Text type="secondary">— {o.institutionName}</Text>
        </span>
      ) : (
        o.name
      ),
  }));

const bySearch = (input, o) =>
  (o?.search || "").includes((input || "").toLowerCase());

/**
 * The « Autre… » control of one field, by kind. `value` is the custom value already decided
 * (undefined when the operator has just opened the control). Text saves on blur / Enter,
 * everything else on change.
 */
export function CustomControl({
  meta,
  options = {},
  value,
  before = [],
  disabled,
  onSave,
}) {
  const text =
    typeof value === "string" || typeof value === "number" ? String(value) : "";
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  const commit = () => {
    const v = draft.trim();
    if (v !== text) onSave(v);
  };
  const textInput = placeholder => (
    <Input
      autoFocus
      disabled={disabled}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={e => e.target.blur()}
      placeholder={placeholder}
    />
  );

  switch (meta.kind) {
    case "text":
      return textInput("Saisir une valeur…");
    case "number":
      return textInput("ex. 100013");
    case "image":
      return (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          {textInput("nom du fichier")}
          <Text type="secondary">
            logo : fichier existant — nom d'un fichier déjà téléversé sur le
            serveur
          </Text>
        </Space>
      );
    case "boolean":
      return (
        <Switch
          disabled={disabled}
          checked={value === true}
          checkedChildren="oui"
          unCheckedChildren="non"
          onChange={b => onSave(b)}
        />
      );
    case "notification":
      return (
        <Space>
          <Switch
            disabled={disabled}
            checked={!!(value && value.cloturePrestation)}
            checkedChildren="oui"
            unCheckedChildren="non"
            onChange={b => onSave({ cloturePrestation: b })}
          />
          <Text>clôture prestation</Text>
        </Space>
      );
    case "enum":
      return (
        <Select
          autoFocus
          showSearch
          allowClear
          disabled={disabled}
          style={{ width: "100%" }}
          optionFilterProp="label"
          placeholder="Choisir…"
          options={enumOptions(meta, before)}
          value={typeof value === "string" && value ? value : undefined}
          onChange={v => onSave(v === undefined ? "" : v)}
        />
      );
    case "ref":
      return (
        <Select
          autoFocus
          showSearch
          allowClear
          disabled={disabled}
          style={{ width: "100%" }}
          placeholder="Choisir…"
          filterOption={bySearch}
          options={refOptions(
            refList(meta, options),
            meta.ref === "Address" || meta.ref === "Location",
          )}
          value={typeof value === "string" && value ? value : undefined}
          onChange={v => onSave(v === undefined ? null : v)}
        />
      );
    case "refs":
      return (
        <Select
          autoFocus
          mode="multiple"
          showSearch
          allowClear
          disabled={disabled}
          style={{ width: "100%" }}
          placeholder="Choisir…"
          maxTagCount="responsive"
          filterOption={bySearch}
          options={refOptions(refList(meta, options), false)}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={list => onSave(list)}
        />
      );
    default:
      return null;
  }
}
